import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Clock } from "@phosphor-icons/react";

/**
 * Time range picker — 3 fields cleanly aligned:
 *   • Date : native date input (open native picker on click)
 *   • Début : dropdown (06:00 → 22:00 by 15 min)
 *   • Fin   : dropdown (start+15min → 23:45 by 15 min)
 *
 * Bindings — parent uses `{scheduled_date: "YYYY-MM-DDTHH:mm", duration_min}`.
 * Changing Fin recomputes Durée automatically. Local ISO string (no UTC drift).
 */
const START_HOUR = 6;
const END_HOUR = 22;
const STEP = 15;

function buildSlots(fromH, toH) {
  const list = [];
  for (let h = fromH; h <= toH; h += 1) {
    for (let m = 0; m < 60; m += STEP) {
      if (h === toH && m > 0) break;
      list.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return list;
}

const START_SLOTS = buildSlots(START_HOUR, END_HOUR);

function parseLocal(str) {
  // "YYYY-MM-DDTHH:mm" → { date: "YYYY-MM-DD", time: "HH:mm" }
  if (!str) return { date: "", time: "" };
  const [d, t] = String(str).split("T");
  return { date: d || "", time: (t || "").slice(0, 5) };
}

function addMinutes(time, minutes) {
  const [h, m] = (time || "00:00").split(":").map(Number);
  const total = h * 60 + m + minutes;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

function diffMinutes(startT, endT) {
  const [sh, sm] = startT.split(":").map(Number);
  const [eh, em] = endT.split(":").map(Number);
  let d = eh * 60 + em - (sh * 60 + sm);
  if (d <= 0) d += 24 * 60; // rolls over midnight
  return d;
}

export default function TimeRangeInput({ scheduled_date, duration_min, onChange, testid = "iv-time" }) {
  const { date, time } = parseLocal(scheduled_date);
  const dur = Math.max(STEP, Math.min(24 * 60, Number(duration_min) || 60));
  const endTime = time ? addMinutes(time, dur) : "";

  const endSlots = useMemo(() => {
    if (!time) return START_SLOTS.slice(1);
    // End slots must be strictly after start
    const startIdx = START_SLOTS.indexOf(time);
    const base = startIdx >= 0 ? START_SLOTS.slice(startIdx + 1) : START_SLOTS.slice(1);
    // Extend up to 23:45 if start > 22:00
    const last = base[base.length - 1] || time;
    const extras = [];
    let cur = addMinutes(last, STEP);
    while (cur !== "00:00" && cur.localeCompare(endTime || cur) <= 0) {
      extras.push(cur);
      const next = addMinutes(cur, STEP);
      if (next === cur) break;
      cur = next;
    }
    // Ensure endTime is in the list even if it's out of range (defensive)
    const set = new Set([...base, ...extras]);
    if (endTime && !set.has(endTime)) set.add(endTime);
    return Array.from(set).sort();
  }, [time, endTime]);

  const emit = (nextDate, nextTime, nextDur) => {
    const iso = nextDate && nextTime ? `${nextDate}T${nextTime}` : "";
    onChange({ scheduled_date: iso, duration_min: nextDur });
  };

  const onDate = (v) => emit(v, time || "09:00", dur);
  const onStart = (v) => emit(date, v, dur);
  const onEnd = (v) => {
    if (!time) return emit(date, "09:00", 60);
    const newDur = diffMinutes(time, v);
    emit(date, time, newDur);
  };

  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50">
        <Clock size={11} weight="duotone" /> Créneau
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-white/50 text-[9px] uppercase tracking-widest">Date</Label>
          <input
            type="date"
            value={date}
            onChange={(e) => onDate(e.target.value)}
            className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:border-[#005CFF]"
            data-testid={`${testid}-date`}
          />
        </div>
        <div>
          <Label className="text-white/50 text-[9px] uppercase tracking-widest">Début</Label>
          <select
            value={time || ""}
            onChange={(e) => onStart(e.target.value)}
            className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-2 text-sm text-white focus:outline-none focus:border-[#005CFF] appearance-none cursor-pointer"
            data-testid={`${testid}-start`}
          >
            <option value="" disabled>—</option>
            {START_SLOTS.map((s) => (
              <option key={s} value={s} className="bg-[#0a0a0a] text-white">{s}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-white/50 text-[9px] uppercase tracking-widest">Fin</Label>
          <select
            value={endTime || ""}
            onChange={(e) => onEnd(e.target.value)}
            disabled={!time}
            className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-2 text-sm text-white focus:outline-none focus:border-[#005CFF] appearance-none cursor-pointer disabled:opacity-40"
            data-testid={`${testid}-end`}
          >
            <option value="" disabled>—</option>
            {endSlots.map((s) => (
              <option key={s} value={s} className="bg-[#0a0a0a] text-white">{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="text-[10px] text-white/40 pt-1">
        Durée : <span className="tabular-nums text-white/70">{Math.floor(dur / 60)}h{String(dur % 60).padStart(2, "0")}</span>
        {" "}<span className="text-white/25">·</span> pas de {STEP} min
      </div>
    </div>
  );
}
